import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FileArrowUp, DownloadSimple, Trash, Image as ImageIcon, FileText,
  UserCircle, Package, MagnifyingGlass, X, ListChecks, Folders, Lock, Warning,
} from "@phosphor-icons/react";
import { http, formatApiError, API } from "../lib/api";
import { shortenFileName } from "../lib/utils";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const IMG_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);
const PDF_EXTS = new Set(["pdf"]);

const UNASSIGNED_KEY = "__unassigned__";
const UNASSIGNED_LABEL = "Unassigned (no phase)";

function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Icon({ ext, size = 16 }) {
  const e = (ext || "").toLowerCase();
  if (IMG_EXTS.has(e)) return <ImageIcon size={size} weight="bold" className="text-blue-600" />;
  if (PDF_EXTS.has(e)) return <FileText size={size} weight="bold" className="text-red-600" />;
  return <FileText size={size} weight="bold" className="text-zinc-500" />;
}

export default function ProjectDeliverables({ projectName, canUpload, canDelete, publicToken, forceLoad, paymentGate = false }) {
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]); // task list for THIS project (upload dropdown source)
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [pickedTaskId, setPickedTaskId] = useState(""); // "" = unassigned
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showGateDialog, setShowGateDialog] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectName) return;
    try {
      if (publicToken) return; // parent injects via forceLoad
      const [dRes, tRes] = await Promise.all([
        http.get(`/projects/${encodeURIComponent(projectName)}/deliverables`),
        canUpload
          ? http.get("/tasks").catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setItems(dRes.data);
      setTasks((tRes.data || []).filter((t) => t.project === projectName));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [projectName, publicToken, canUpload]);

  useEffect(() => { if (!publicToken) load(); }, [load, publicToken]);
  useEffect(() => { if (forceLoad) setItems(forceLoad); }, [forceLoad]);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption);
      if (pickedTaskId) form.append("task_id", pickedTaskId);
      await http.post(`/projects/${encodeURIComponent(projectName)}/deliverables`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Deliverable uploaded");
      setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = (item) => setPendingDelete(item);
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await http.delete(`/deliverables/${pendingDelete.id}`);
      toast.success("Deliverable deleted");
      setPendingDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const list = useMemo(
    () => (publicToken ? (forceLoad || []) : items),
    [publicToken, forceLoad, items]
  );

  const downloadUrl = (id) =>
    publicToken
      ? `${API}/public/projects/${publicToken}/deliverables/${id}/download`
      : `${API}/deliverables/${id}/download`;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) =>
      (d.task_title || "").toLowerCase().includes(q) ||
      (d.name || "").toLowerCase().includes(q) ||
      (d.caption || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  const grouped = useMemo(() => {
    const g = new Map();
    filtered.forEach((d) => {
      const key = d.task_id || UNASSIGNED_KEY;
      const label = d.task_title || UNASSIGNED_LABEL;
      if (!g.has(key)) g.set(key, { key, label, items: [] });
      g.get(key).items.push(d);
    });
    // Keep unassigned last; others sorted alphabetically
    return Array.from(g.values()).sort((a, b) => {
      if (a.key === UNASSIGNED_KEY) return 1;
      if (b.key === UNASSIGNED_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filtered]);

  return (
    <div className="border border-zinc-300 bg-white" data-testid="project-deliverables">
      <div className="px-4 sm:px-5 py-3 border-b border-zinc-300 bg-zinc-50">
        <div className="flex items-center gap-2 flex-wrap">
          <Package size={14} weight="bold" className="text-orange-600" />
          <span className="label-overline text-zinc-900">Project Deliverables</span>
          <span className="label-overline bg-zinc-900 text-white px-2 py-0.5" data-testid="deliverable-count">
            {list.length}/100
          </span>
          {list.length > 0 && (
            <div className="ml-auto relative flex-1 sm:flex-none sm:w-72 min-w-[180px]">
              <MagnifyingGlass size={13} weight="bold"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by task title / file name…"
                data-testid="deliverable-search-input"
                className="w-full h-8 pl-8 pr-8 border border-zinc-300 bg-white text-xs focus:outline-none focus:border-blue-600"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  data-testid="deliverable-search-clear"
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          )}
        </div>

        {canUpload && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(180px,1fr)_minmax(140px,220px)_auto] gap-2 items-center">
            <input
              type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional caption for next upload…"
              className="h-9 rounded-none border border-zinc-300 px-2 text-xs focus:outline-none focus:border-blue-600 bg-white"
              data-testid="deliverable-caption"
            />
            <select
              value={pickedTaskId}
              onChange={(e) => setPickedTaskId(e.target.value)}
              data-testid="deliverable-task-select"
              className="h-9 rounded-none border border-zinc-300 px-2 text-xs bg-white focus:outline-none focus:border-blue-600"
            >
              <option value="">— No specific task —</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
            <button
              onClick={pick}
              disabled={uploading || list.length >= 100}
              data-testid={`upload-deliverable-${projectName}`}
              className="h-9 flex items-center justify-center gap-1.5 label-overline border border-orange-600 bg-orange-600 text-white px-4 hover:bg-orange-700 disabled:opacity-50"
            >
              <FileArrowUp size={12} weight="bold" />
              {uploading ? "Uploading…" : "Upload"}
            </button>
            <input ref={inputRef} type="file" hidden onChange={upload} />
          </div>
        )}
      </div>

      {paymentGate && (
        <div data-testid="deliverables-payment-gate-banner"
             className="border-b border-orange-300 bg-orange-50 px-4 sm:px-5 py-3 flex items-center gap-2 text-sm text-orange-900">
          <Warning size={14} weight="bold" className="text-orange-600 shrink-0" />
          <div>
            <strong>Payment pending.</strong> Downloads are temporarily locked. Please deposit the previous invoice bill first — the studio will unlock deliverables once the invoice is marked paid.
          </div>
        </div>
      )}

      {list.length === 0 && (
        <div className="p-8 text-center label-overline text-zinc-400" data-testid="deliverables-empty">
          No deliverables yet
        </div>
      )}

      {list.length > 0 && filtered.length === 0 && (
        <div className="p-6 text-center label-overline text-zinc-500" data-testid="deliverables-search-empty">
          No deliverables match &quot;{search}&quot;
        </div>
      )}

      {grouped.map((g) => (
        <div key={g.key} data-testid={`deliverable-group-${g.key}`}
             className="border-t border-zinc-200 first:border-t-0">
          <div className="px-4 sm:px-5 py-2 bg-zinc-100/70 border-b border-zinc-200 flex items-center gap-2">
            {g.key === UNASSIGNED_KEY
              ? <Folders size={12} weight="bold" className="text-zinc-500" />
              : <ListChecks size={12} weight="bold" className="text-blue-600" />}
            <span className="label-overline text-zinc-800">{g.label}</span>
            <span className="ml-auto label-overline text-zinc-500">{g.items.length} file{g.items.length === 1 ? "" : "s"}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
            {g.items.map((d, i) => (
              <div key={d.id} data-testid={`deliverable-${d.id}`}
                   className={`p-4 border-zinc-200 ${i % 3 !== 2 ? "sm:border-r" : ""} border-b`}>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 border border-zinc-300 bg-zinc-50 flex items-center justify-center">
                    <Icon ext={d.ext} size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-900 truncate" title={d.name}>
                      {shortenFileName(d.name, 32)}
                    </div>
                    {d.caption && (
                      <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{d.caption}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-zinc-500 font-mono flex-wrap">
                      <span className="flex items-center gap-1">
                        <UserCircle size={10} weight="bold" /> {d.uploaded_by_name}
                      </span>
                      <span>·</span>
                      <span>{bytes(d.size || 0)}</span>
                      {d.created_at && (
                        <>
                          <span>·</span>
                          <span>{new Date(d.created_at).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                  {paymentGate ? (
                    <button
                      type="button"
                      onClick={() => setShowGateDialog(true)}
                      data-testid={`download-blocked-${d.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 label-overline bg-zinc-300 text-zinc-600 py-1.5 cursor-not-allowed"
                      aria-disabled="true"
                      title="Payment pending — download locked"
                    >
                      <Lock size={12} weight="bold" /> Locked
                    </button>
                  ) : (
                    <a href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer"
                       data-testid={`download-deliverable-${d.id}`}
                       className="flex-1 flex items-center justify-center gap-1.5 label-overline bg-zinc-900 text-white hover:bg-blue-600 py-1.5">
                      <DownloadSimple size={12} weight="bold" /> Download
                    </a>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(d)}
                            data-testid={`delete-deliverable-${d.id}`}
                            className="p-1.5 text-zinc-400 hover:text-red-600" aria-label="Delete">
                      <Trash size={13} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Delete deliverable?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will permanently remove <strong className="break-all">&quot;{pendingDelete?.name}&quot;</strong> from
              project <strong>{projectName}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-deliverable"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-deliverable"
              disabled={deleting}
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs"
            >{deleting ? "Deleting…" : "Yes, Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showGateDialog} onOpenChange={setShowGateDialog}>
        <AlertDialogContent className="rounded-none border-2 border-orange-600 shadow-brutal" data-testid="payment-gate-dialog">
          <AlertDialogHeader>
            <div className="label-overline text-orange-600 mb-1 flex items-center gap-1.5">
              <Lock size={12} weight="bold" /> Payment Required
            </div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Please deposit previous invoice bill first
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-700 leading-relaxed">
              A milestone invoice for this project has been unpaid for more than 7 days. Please deposit the previous invoice bill first — deliverables will be unlocked automatically once the studio marks the payment as received. Contact us if you have already paid so we can update your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              data-testid="payment-gate-ok"
              className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs"
            >Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
